import React, { useState, useEffect } from "react";
import config from "../config/config";

const API_URL = config.API_URL;

function CommuteTimeEditModal({ date, userName, userId, initialData, onClose, onUpdate }) {
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [checkInOutside, setCheckInOutside] = useState(false);
  const [checkOutOutside, setCheckOutOutside] = useState(false);

  useEffect(() => {
    // 초기값 설정 - 항상 모든 값을 리셋
    if (initialData?.checkIn) {
      setCheckInTime(initialData.checkIn.time || "");
      // 체크박스: true=관내(0), false=관외(1) 이므로 반전
      setCheckInOutside(!initialData.checkIn.isOutside);
    } else {
      setCheckInTime("");
      setCheckInOutside(false);
    }

    if (initialData?.checkOut) {
      setCheckOutTime(initialData.checkOut.time || "");
      // 체크박스: true=관내(0), false=관외(1) 이므로 반전
      setCheckOutOutside(!initialData.checkOut.isOutside);
    } else {
      setCheckOutTime("");
      setCheckOutOutside(false);
    }
  }, [initialData]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/commute/updateCommuteTime`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          date: date,
          checkInTime: checkInTime || null,
          checkOutTime: checkOutTime || null,
          // 체크박스가 체크된 경우(true) = 관내(false), 미체크(false) = 관외(true)
          checkInOutside: !checkInOutside,
          checkOutOutside: !checkOutOutside,
          user_id: userId, // 수정할 사용자 ID 전달
        }),
      });

      const result = await response.json();

      if (result.result) {
        // 먼저 데이터 새로고침
        if (onUpdate) {
          await onUpdate();
        }
        // 모달 닫기
        onClose();
        // 마지막에 알림 (모달이 닫힌 후)
        setTimeout(() => {
          alert("출퇴근 시간이 수정되었습니다.");
        }, 100);
      } else {
        alert(result.message || "시간 수정에 실패했습니다.");
      }
    } catch (error) {
      console.error("Error updating commute time:", error);
      alert("시간 수정 중 오류가 발생했습니다.");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const weekday = weekdays[d.getDay()];
    return `${year}년 ${month}월 ${day}일 (${weekday})`;
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>{formatDate(date)}</h3>
            <p style={styles.subtitle}>{userName}</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={styles.content}>
          {/* 출근 시간 수정 */}
          <div style={styles.timeSection}>
            <div style={styles.label}>
              <span style={styles.labelIcon}>🏢</span>
              <span style={styles.labelText}>출근 시간</span>
            </div>
            <input
              type="time"
              value={checkInTime}
              onChange={(e) => setCheckInTime(e.target.value)}
              style={styles.timeInput}
              placeholder="미입력"
            />
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={checkInOutside}
                onChange={(e) => setCheckInOutside(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>관내 출근</span>
            </label>
          </div>

          {/* 퇴근 시간 수정 */}
          <div style={styles.timeSection}>
            <div style={styles.label}>
              <span style={styles.labelIcon}>🏠</span>
              <span style={styles.labelText}>퇴근 시간</span>
            </div>
            <input
              type="time"
              value={checkOutTime}
              onChange={(e) => setCheckOutTime(e.target.value)}
              style={styles.timeInput}
              placeholder="미입력"
            />
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={checkOutOutside}
                onChange={(e) => setCheckOutOutside(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>관내 퇴근</span>
            </label>
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose}>
            취소
          </button>
          <button style={styles.saveButton} onClick={handleSave}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "420px",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",
  },
  header: {
    padding: "20px 24px",
    borderBottom: "1px solid #E5E5E5",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#232323",
    marginBottom: "4px",
  },
  subtitle: {
    margin: 0,
    fontSize: "14px",
    color: "#666",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#999",
    cursor: "pointer",
    padding: "0",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: "24px",
  },
  timeSection: {
    marginBottom: "20px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
  },
  labelIcon: {
    fontSize: "20px",
    marginRight: "8px",
  },
  labelText: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#666",
  },
  timeInput: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    fontWeight: "500",
    border: "2px solid #E0E0E0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    marginTop: "8px",
    cursor: "pointer",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    marginRight: "8px",
    cursor: "pointer",
  },
  checkboxText: {
    fontSize: "14px",
    color: "#666",
  },
  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #E5E5E5",
    display: "flex",
    gap: "12px",
  },
  cancelButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#666",
    cursor: "pointer",
  },
  saveButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#686769",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
  },
};

export default CommuteTimeEditModal;
